import type { ReactNode } from 'react';
import type { StackedField } from '../../../../ConsolePanel';
import type { SegmentReading } from '@/state/experiments/segment-rows';
import { cn } from 'helpers/cn';
import { formatMinorUnits } from 'helpers/formatters';
import { GRID_TABLE_ROW, GRID_TABLE_ROWGROUP, GridTable, StackedRow } from '../../../../ConsolePanel';

type SegmentTableProps = {
  label: string;
  readings: readonly SegmentReading[];
  /** Null when revenue needs a single currency to mean anything. */
  currency: string | null;
  showRevenue: boolean;
  /** Per variant, the per-session rate from the main results, for the "all visitors" table only. */
  mainRates?: ReadonlyMap<number, number | null>;
  heading?: ReactNode;
};

const TEMPLATE_BASE = 'minmax(160px,1fr) repeat(3, minmax(90px,auto)) minmax(110px,auto) minmax(90px,auto)';

const percent = (value: number): string => `${(value * 100).toFixed(2)}%`;
const signed = (value: number): string => `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;

type CellProps = { children: ReactNode; className?: string; title?: string };

const Cell = ({ children, className, title }: CellProps) => (
  <div role="cell" title={title} className={cn('num text-foreground', className)}>
    {children}
  </div>
);

const Head = ({ children, className }: CellProps) => (
  <div role="columnheader" className={cn('micro-label', className)}>
    {children}
  </div>
);

const liftText = (reading: SegmentReading): string => {
  if (reading.isControl) return 'baseline';
  if (reading.noLift === 'too-few') return 'too few';
  if (reading.noLift === 'no-baseline' || reading.lift === null) return 'no baseline';
  return signed(reading.lift);
};

/**
 * One table of per-visitor readings, per variant. Readings under the minimum sample are
 * dimmed and show no lift: at that size a lift is noise that reads like a finding.
 *
 * Lift is never coloured as a win or a loss here. Segments are exploratory, and a green
 * number on one of six segment rows is how a losing test gets reported as a win.
 */
export const SegmentTable = ({ label, readings, currency, showRevenue, mainRates, heading }: SegmentTableProps) => {
  const withMain = mainRates !== undefined;
  const template = withMain ? `${TEMPLATE_BASE} minmax(110px,auto)` : TEMPLATE_BASE;

  const rows = readings.map((reading) => ({
    reading,
    fields: [
      { key: 'visitors', label: 'Visitors', value: reading.enrolled.toLocaleString() },
      { key: 'converted', label: 'Converted', value: reading.converters.toLocaleString() },
      { key: 'rate', label: 'Conv. rate', value: percent(reading.rate) },
      {
        key: 'rpv',
        label: 'Rev. per visitor',
        value: showRevenue ? formatMinorUnits(reading.revenuePerVisitor, currency) : 'pick a currency',
        className: showRevenue ? undefined : 'text-dim',
      },
      {
        key: 'lift',
        label: 'Lift',
        value: liftText(reading),
        className: reading.lift === null ? 'text-dim' : undefined,
      },
      ...(withMain
        ? [
            {
              key: 'main',
              label: 'Main results rate',
              value: (() => {
                const main = mainRates.get(reading.variantId);
                return main == null ? 'n/a' : percent(main);
              })(),
              className: 'text-dim',
            },
          ]
        : []),
    ] satisfies StackedField[],
  }));

  return (
    <div className="border-line bg-panel2 rounded-card border">
      {heading && <div className="border-line border-b px-4 py-2.5">{heading}</div>}

      <div className="flex flex-col gap-2 p-3 md:hidden">
        {rows.map(({ reading, fields }) => (
          <StackedRow
            key={reading.variantId}
            className={cn(reading.tooSmall && 'opacity-60')}
            heading={
              <div className="flex min-w-0 items-baseline gap-2">
                <span className="text-foreground truncate text-body" title={reading.name}>
                  {reading.name}
                </span>
                {reading.isControl && <span className="micro-label shrink-0">control</span>}
              </div>
            }
            fields={fields}
          />
        ))}
      </div>

      <GridTable label={label} minWidth={withMain ? 820 : 700} template={template}>
        <div role="row" className={cn(GRID_TABLE_ROW, 'border-line items-center border-b py-2')}>
          <Head>Variant</Head>
          <Head className="text-right">Visitors</Head>
          <Head className="text-right">Converted</Head>
          <Head className="text-right">Conv. rate</Head>
          <Head className="text-right">Rev. per visitor</Head>
          <Head className="text-right">Lift</Head>
          {withMain && <Head className="text-right">Main results rate</Head>}
        </div>
        <div role="rowgroup" className={GRID_TABLE_ROWGROUP}>
          {rows.map(({ reading, fields }) => (
            <div
              key={reading.variantId}
              role="row"
              className={cn(
                GRID_TABLE_ROW,
                'border-line items-center border-b py-2 last:border-b-0',
                reading.tooSmall && 'opacity-60',
              )}
            >
              <Cell className="truncate font-sans text-body" title={reading.name}>
                {reading.name}
                {reading.isControl && <span className="micro-label ml-2">control</span>}
              </Cell>
              {fields.map((field) => (
                <Cell key={field.key} className={cn('text-right', field.className)}>
                  {field.value}
                </Cell>
              ))}
            </div>
          ))}
        </div>
      </GridTable>
    </div>
  );
};
